import { useRef, useState, useEffect } from "react";
import { toast } from "sonner";
import { FileDown, FileSpreadsheet, CloudUpload, Printer, Loader2 } from "lucide-react";
import { api } from "../lib/api";

let _driveConnected = null; // cache module-level

async function checkDrive() {
  if (_driveConnected !== null) return _driveConnected;
  try {
    const { data } = await api.get("/admin/drive/connected");
    _driveConnected = !!data.connected;
  } catch { _driveConnected = false; }
  return _driveConnected;
}

async function archiveBlob(blob, filename, subfolder) {
  try {
    const fd = new FormData();
    fd.append("file", blob, filename);
    fd.append("subfolder", subfolder);
    await api.post("/admin/drive/archive-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return true;
  } catch { return false; }
}

const slug = (s) => (s || "module").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-|-$/g, "").toLowerCase();

async function buildPdf(node) {
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import("html2canvas"), import("jspdf")]);
  const canvas = await html2canvas(node, { scale: 2, backgroundColor: "#ffffff", useCORS: true, logging: false });
  const img = canvas.toDataURL("image/jpeg", 0.92);
  const pdf = new jsPDF("p", "mm", "a4");
  const pw = pdf.internal.pageSize.getWidth();
  const ph = pdf.internal.pageSize.getHeight();
  const imgH = (canvas.height * pw) / canvas.width;
  let left = imgH;
  let pos = 0;
  pdf.addImage(img, "JPEG", 0, pos, pw, imgH);
  left -= ph;
  while (left > 0) {
    pos -= ph;
    pdf.addPage();
    pdf.addImage(img, "JPEG", 0, pos, pw, imgH);
    left -= ph;
  }
  return pdf.output("blob");
}

async function buildXlsx(node, title) {
  const XLSX = await import("xlsx");
  const tables = node.querySelectorAll("table");
  const wb = XLSX.utils.book_new();
  if (tables.length) {
    tables.forEach((t, i) => {
      const ws = XLSX.utils.table_to_sheet(t, { raw: true });
      XLSX.utils.book_append_sheet(wb, ws, `Tableau ${i + 1}`.slice(0, 31));
    });
  } else {
    const ws = XLSX.utils.aoa_to_sheet([[title], ["Aucun tableau détecté dans ce module."],
      ["Utilisez PDF/Imprimer pour un rendu visuel complet."]]);
    XLSX.utils.book_append_sheet(wb, ws, "Export");
  }
  const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  return new Blob([out], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
}

function printNode(node, title) {
  const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
    .map((n) => n.outerHTML).join("\n");
  const html = `<!DOCTYPE html><html><head><title>${title}</title>${styles}
    <style>body{padding:24px;background:#fff}@page{margin:12mm}</style></head>
    <body>${node.outerHTML}</body></html>`;
  const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
  const w = window.open(url, "_blank", "width=1024,height=768");
  if (!w) { URL.revokeObjectURL(url); toast.error("Autorisez les fenêtres popup pour imprimer"); return; }
  w.addEventListener("load", () => { w.focus(); w.print(); setTimeout(() => URL.revokeObjectURL(url), 4000); });
}

export function ModuleToolbar({ targetRef, getNode: getNodeProp, title = "Module", subfolder = "Rapports" }) {
  const [busy, setBusy] = useState("");
  const [drive, setDrive] = useState(false);
  useEffect(() => { checkDrive().then(setDrive); }, []);

  const node = () => (getNodeProp ? getNodeProp() : targetRef?.current);
  const fname = (ext) => `${slug(title)}-${new Date().toISOString().slice(0, 10)}.${ext}`;
  const download = (blob, name) => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = name; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  };

  const onPdf = async () => {
    if (!node()) return;
    setBusy("pdf");
    try {
      const blob = await buildPdf(node());
      const name = fname("pdf");
      download(blob, name);
      const arch = await archiveBlob(blob, name, subfolder);
      toast.success(arch ? "PDF téléchargé et archivé dans Google Drive" : "PDF téléchargé");
    } catch (e) { toast.error("Export PDF impossible"); } finally { setBusy(""); }
  };

  const onXlsx = async () => {
    if (!node()) return;
    setBusy("xlsx");
    try {
      const blob = await buildXlsx(node(), title);
      const name = fname("xlsx");
      download(blob, name);
      const arch = await archiveBlob(blob, name, subfolder);
      toast.success(arch ? "Excel téléchargé et archivé dans Google Drive" : "Excel téléchargé");
    } catch (e) { toast.error("Export Excel impossible"); } finally { setBusy(""); }
  };

  const onArchive = async () => {
    if (!node()) return;
    if (!drive) { toast.info("Connectez Google Drive (onglet Google Drive) pour archiver"); return; }
    setBusy("arch");
    try {
      const blob = await buildPdf(node());
      const ok = await archiveBlob(blob, fname("pdf"), subfolder);
      toast[ok ? "success" : "error"](ok ? `Archivé dans Google Drive · BASE VITALIS STORE / ${subfolder}` : "Archivage impossible");
    } catch (e) { toast.error("Archivage impossible"); } finally { setBusy(""); }
  };

  const onPrint = () => { if (node()) printNode(node(), title); };

  const Btn = ({ id, icon: Icon, label, onClick, cls }) => (
    <button onClick={onClick} disabled={!!busy} data-testid={`toolbar-${id}`}
      className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${cls}`}>
      {busy === id ? <Loader2 size={13} className="animate-spin" /> : <Icon size={13} />} {label}
    </button>
  );

  return (
    <div className="flex flex-wrap items-center justify-end gap-2" data-testid="module-toolbar">
      <Btn id="pdf" icon={FileDown} label="PDF" onClick={onPdf} cls="border-red-200 bg-red-50 text-red-700 hover:bg-red-100" />
      <Btn id="xlsx" icon={FileSpreadsheet} label="Excel" onClick={onXlsx} cls="border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" />
      <Btn id="arch" icon={CloudUpload} label="Archive" onClick={onArchive} cls="border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100" />
      <Btn id="print" icon={Printer} label="Imprimer" onClick={onPrint} cls="border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100" />
    </div>
  );
}

// Enveloppe un module : barre d'outils (PDF/Excel/Archive/Imprimer) en haut à droite + contenu exportable.
export function ModuleFrame({ title = "Module", subfolder = "Gestionnaire", children }) {
  const ref = useRef(null);
  return (
    <div>
      <div className="mb-3 flex justify-end">
        <ModuleToolbar targetRef={ref} title={title} subfolder={subfolder} />
      </div>
      <div ref={ref}>{children}</div>
    </div>
  );
}
